/**
 * tabs.js — generic click-to-switch tabs for diagram panels.
 *
 * Markup contract (see the With/Without Stance section):
 *   <div data-diagram-tabs>
 *     <button data-tab="before" class="is-active">…</button>
 *     <button data-tab="after">…</button>
 *     <div data-panel="before">…</div>
 *     <div data-panel="after" class="hidden">…</div>
 *   </div>
 *
 * Clicking a tab marks it `.is-active` (+ aria-selected) and shows the panel
 * whose `data-panel` matches the tab's `data-tab`, hiding the others.
 */

function setupDiagramTabs() {
  document.querySelectorAll('[data-diagram-tabs]').forEach((root) => {
    const tabs = [...root.querySelectorAll('[data-tab]')];
    const panels = [...root.querySelectorAll('[data-panel]')];
    if (!tabs.length || !panels.length) return;

    // Optional slider switch (Before/After Stance). When present, its knob
    // position + track colour are driven purely by `data-diagram-state` on
    // the root (see main.css), and clicking it toggles to the other side.
    const sw = root.querySelector('[data-diagram-switch]');

    const activate = (key) => {
      tabs.forEach((t) => {
        const on = t.getAttribute('data-tab') === key;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', String(on));
      });
      // Drive display directly so the panel's own layout class (flex) applies
      // when shown — avoids fighting Tailwind's `hidden` over `flex`.
      panels.forEach((p) => {
        p.style.display = p.getAttribute('data-panel') === key ? '' : 'none';
      });
      // Drive the switch (knob slide + track colour via CSS) + a11y state.
      root.setAttribute('data-diagram-state', key);
      if (sw) sw.setAttribute('aria-checked', String(key === 'after'));
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => activate(tab.getAttribute('data-tab')));
    });

    // The switch toggles to the opposite of the current side.
    if (sw) {
      sw.addEventListener('click', () => {
        const cur = root.getAttribute('data-diagram-state') || 'before';
        activate(cur === 'before' ? 'after' : 'before');
      });
    }

    // Initialise from the root's declared state, else the active tab, else first.
    const start =
      root.getAttribute('data-diagram-state') ||
      (root.querySelector('[data-tab].is-active') || tabs[0]).getAttribute('data-tab');
    activate(start);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupDiagramTabs);
} else {
  setupDiagramTabs();
}
